/**
 * ViduOptionsPanel - Collapsible settings panel for Vidu AI video generation.
 * Shows model selection, duration, aspect ratio, style, audio toggle, and cost estimate.
 */

import { Music, ChevronDown, ChevronUp } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import {
  VIDU_MODEL_OPTIONS,
  VIDU_ASPECT_RATIO_OPTIONS,
  VIDU_STYLE_OPTIONS,
  calculateViduTokens,
  supportsViduAudio,
  getViduAvailableDurations,
  type ViduConfig,
  type ViduModel,
  type ViduDuration,
  type ViduAspectRatio,
  type ViduStyle,
} from '@/constants/viduVideo';
import { SegmentedPicker } from '../../common/SegmentedPicker';
import type { SegmentOption } from '../../common/SegmentedPicker';

interface ViduOptionsPanelProps {
  viduConfig: ViduConfig;
  durationMinutes: number;
  showAdvanced: boolean;
  onToggleAdvanced: () => void;
  onChange: (config: ViduConfig) => void;
  disabled: boolean;
  /** Hide cost estimate (e.g. when parent already shows unified cost) */
  showCost?: boolean;
}

export function ViduOptionsPanel({
  viduConfig,
  durationMinutes,
  showAdvanced,
  onToggleAdvanced,
  onChange,
  disabled,
  showCost = true,
}: ViduOptionsPanelProps) {
  const { t } = useTranslation();

  const availableDurations = getViduAvailableDurations(viduConfig.model);

  const handleModelChange = (model: ViduModel) => {
    const updates: Partial<ViduConfig> = { model };
    const newDurations = getViduAvailableDurations(model);
    if (!newDurations.includes(viduConfig.duration)) {
      updates.duration = newDurations[newDurations.length - 1];
    }
    if (!supportsViduAudio(model)) {
      updates.enable_audio = false;
    }
    onChange({ ...viduConfig, ...updates });
  };

  const modelOptions: SegmentOption[] = VIDU_MODEL_OPTIONS.map((option) => ({
    value: option.value,
    label: option.label,
    description: option.description,
    // `null` tokens mean the server has no price for this model at all (the Q3
    // ids raise ViduModelUnavailableError), so the row says so instead of
    // rendering a number nobody can be charged.
    trailingMeta:
      option.tokens4s === null
        ? t('videoSource.priceUnavailable', { defaultValue: 'No price yet' })
        : `${option.tokens4s.toLocaleString()}-${(option.tokens8s ?? option.tokens4s).toLocaleString()} tokens`,
    // Q3 / Q3-Fast are gated until the official api.vidu.com slug is verified
    // (see docs/modules/VIDU_Q3_ROUTING.md). Backend raises ViduModelUnavailableError
    // if selected, so we surface the unavailability in the picker too.
    disabled: option.disabled === true,
    disabledReason: option.disabledReason,
  }));

  const durationOptions: SegmentOption[] = availableDurations.map((d) => ({
    value: d,
    label: `${d}s`,
  }));

  const aspectRatioOptions: SegmentOption[] = VIDU_ASPECT_RATIO_OPTIONS.map((ratio) => ({
    value: ratio.value,
    label: ratio.label,
  }));

  const styleOptions: SegmentOption[] = VIDU_STYLE_OPTIONS.map((style) => ({
    value: style.value,
    label: style.label,
  }));

  return (
    <div className="border border-white/10 rounded-lg overflow-hidden bg-black/20">
      <button
        type="button"
        onClick={onToggleAdvanced}
        className="w-full flex items-center justify-between p-3 bg-black/20 hover:bg-white/5 transition-colors"
      >
        <span className="font-medium text-white/80 flex items-center space-x-2">
          <Music className="h-4 w-4 text-primary-400" />
          <span>{t('videoSource.viduSettings', { defaultValue: 'Vidu AI Settings' })}</span>
        </span>
        {showAdvanced ? (
          <ChevronUp className="w-5 h-5 text-white/40" />
        ) : (
          <ChevronDown className="w-5 h-5 text-white/40" />
        )}
      </button>

      {showAdvanced && (
        <div className="p-4 space-y-4 border-t border-white/5">
          {/* Model Selection */}
          <SegmentedPicker
            label={t('videoSource.viduModel', { defaultValue: 'Model' })}
            options={modelOptions}
            value={viduConfig.model}
            onChange={(val) => !disabled && handleModelChange(val as ViduModel)}
            size="md"
            layout="equal"
            disabled={disabled}
          />

          {/* Duration Selection */}
          <SegmentedPicker
            label={t('videoSource.viduDuration', { defaultValue: 'Clip Duration' })}
            options={durationOptions}
            value={viduConfig.duration}
            onChange={(val) =>
              !disabled && onChange({ ...viduConfig, duration: val as ViduDuration })
            }
            size="sm"
            layout="equal"
            disabled={disabled}
          />

          {/* Aspect Ratio Selection */}
          <SegmentedPicker
            label={t('videoSource.viduAspectRatio', { defaultValue: 'Aspect Ratio' })}
            options={aspectRatioOptions}
            value={viduConfig.aspect_ratio}
            onChange={(val) =>
              !disabled && onChange({ ...viduConfig, aspect_ratio: val as ViduAspectRatio })
            }
            size="sm"
            layout="equal"
            disabled={disabled}
          />

          {/* Style + Audio row */}
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Style */}
            <div className="flex-1">
              <SegmentedPicker
                label={t('videoSource.viduStyle', { defaultValue: 'Style' })}
                options={styleOptions}
                value={viduConfig.style}
                onChange={(val) =>
                  !disabled && onChange({ ...viduConfig, style: val as ViduStyle })
                }
                size="sm"
                layout="equal"
                disabled={disabled}
              />
            </div>

            {/* Audio Toggle */}
            {supportsViduAudio(viduConfig.model) && (
              <div className="flex-1 flex flex-col gap-2">
                <label className="form-label text-sm font-medium text-white/70">
                  {t('videoSource.enableAudio', { defaultValue: 'Generate Audio' })}
                </label>
                <button
                  type="button"
                  onClick={() =>
                    !disabled && onChange({ ...viduConfig, enable_audio: !viduConfig.enable_audio })
                  }
                  disabled={disabled}
                  className={[
                    'w-full py-2 px-3 rounded-lg border text-sm font-medium min-h-[44px] transition-all',
                    viduConfig.enable_audio
                      ? 'border-primary-500 bg-primary-900/30 text-primary-200'
                      : 'border-white/10 bg-black/20 text-white/60 hover:bg-white/5',
                    disabled ? 'opacity-50 cursor-not-allowed' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  {viduConfig.enable_audio ? 'Enabled' : 'Disabled'}
                </button>
              </div>
            )}
          </div>

          {/* Cost Estimate */}
          {showCost && (
            <div className="mt-4 text-right">
              <div className="text-xs text-white/40">
                {t('videoSource.estimatedCost', { defaultValue: 'Cost' })}
              </div>
              {(() => {
                // A null quote is the server saying it will not price this pair.
                // Showing "0 tokens" for it -- which this did until 2026-09 --
                // advertises a free clip the backend then refuses to render.
                const tokensPerClip = calculateViduTokens(viduConfig.duration, viduConfig.model);
                if (tokensPerClip === null) {
                  return (
                    <div className="text-sm font-semibold text-amber-400">
                      {t('videoSource.priceUnavailable', {
                        defaultValue: 'No price yet',
                      })}
                    </div>
                  );
                }
                const clipDuration = parseInt(viduConfig.duration);
                const numClips = Math.ceil((durationMinutes * 60) / clipDuration);
                return (
                  <>
                    <div className="text-lg font-semibold text-green-400">
                      {(numClips * tokensPerClip).toLocaleString()} tokens
                    </div>
                    <div className="text-xs text-white/40 mt-0.5">
                      {`${numClips} clips x ${tokensPerClip.toLocaleString()} tokens`}
                    </div>
                  </>
                );
              })()}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
